/**
 * The third dimension, and what it is for.
 *
 * A 3D view of a graph is usually a bad trade: occlusion, no stable mental map,
 * and depth cues that fight whatever the geometry was supposed to mean. This one
 * exists because of a fact about *this* graph rather than because 3D is nice.
 *
 * The reasoning graph is a TREE plus BRIDGES. `layout.ts` partitions the circle
 * by subtree weight, which guarantees no two strands ever cross — that is what
 * makes the 2D canvas readable, and it works precisely because a tree is planar.
 * Bridges are the edges that are not in the tree: two distant branches the model
 * thinks fuse. They are the part the tool considers most valuable and the part a
 * planar layout structurally cannot show. In 2D a bridge has to cross the whole
 * canvas, `layout.ts` has to seat a synthesis node outside both its parents just
 * to make the span legible, and a bridge into another exploration cannot be drawn
 * at all — it degrades to a dashed stub pointing at nothing.
 *
 * So: 2D for the argument's skeleton, 3D for the connections across it.
 *
 * ── What this view re-binds, deliberately and out loud ────────────────────
 *
 * The 2D canvas binds NODE SIZE to novelty and confidence and STRAND THICKNESS to
 * confidence. Perspective scales both. Carrying those bindings into a projected
 * view would mean every reading of size became a reading of distance — the exact
 * failure the canvas's own header forbids ("if a mark is drawn, it is because a
 * field in the data changed").
 *
 * Rather than compensate size for depth — which throws away the one cue that
 * makes a 3D scene legible at all — this view gives size and thickness over to
 * distance, and moves the semantic load to channels perspective cannot touch:
 *
 *   hue      →  what kind of move it is        (unchanged from 2D)
 *   glyph    →  open / contradiction / insight / disputed / checked  (unchanged)
 *   HEIGHT   →  how much outside contact the claim has had          (new)
 *
 * Height is the channel this view earns. The 2D canvas has nowhere to put it, and
 * it answers the question the whole tool is built around: how much of this is
 * resting on the model alone? At eye level a graph that has never been checked is
 * visibly a flat disc. That flatness is the finding, not a rendering fault.
 *
 * Novelty and confidence are not lost — they are numbers on the node panel, where
 * they can be read exactly instead of estimated from a radius.
 */

import type { ReasonNode } from './types'
import { checkedAgainstSources } from './types'
import { priorArtSearched } from './priorArt'
import { clamp, type Pt } from './geometry'

export interface Vec3 {
  x: number
  y: number
  z: number
}

/**
 * Where the camera is, as six serialisable numbers.
 *
 * Angles and a distance rather than a matrix, because this is stored in prefs and
 * read by the UI: "which way am I facing" has to survive a reload and has to be
 * nudgeable by a keypress. A matrix would be neither.
 */
export interface Cam {
  /** Rotation about the vertical axis, radians. */
  yaw: number
  /** Elevation, radians. Positive looks down at the plane. */
  pitch: number
  /** Eye distance from `target`, world units. */
  dist: number
  /** What the camera orbits and looks at. */
  target: Vec3
}

/** Straight down the evidence axis is a top view — which is the 2D view with
 *  worse type. Start at an oblique that shows relief and still reads the tree. */
export const CAM_HOME: Cam = {
  yaw: -0.62,
  pitch: 0.5,
  dist: 2400,
  target: { x: 0, y: 120, z: 0 },
}

/**
 * Pitch limits.
 *
 * The top is short of vertical because a true top-down view collapses the height
 * axis to nothing and silently becomes a worse 2D view. The bottom is a little
 * below the plane rather than at it: being able to duck under the disc is how you
 * see that nothing is under it, and a hard floor at zero would make the gesture
 * feel broken instead of bounded.
 */
const PITCH_MIN = -0.32
const PITCH_MAX = 1.42

const DIST_MIN = 340
const DIST_MAX = 12_000

/** Field of view as a focal length in pixels, at a 1000px-wide stage. A long lens
 *  on purpose: wide-angle exaggerates depth, and an exaggerated depth cue is a
 *  lie about how far apart two claims are. */
const FOCAL_AT_1000 = 1180

/* ── the evidence axis ──────────────────────────────────────────────────── */

/**
 * World units per tier of outside contact.
 *
 * Read against `layout.ts`: the first ring is 300 units, and a depth-six graph has
 * a radius somewhere around 1000–1300. Three tiers at this height put a fully
 * grounded claim at roughly a third of that radius — enough relief to read from any
 * orbit, not enough to turn the tree into a tower and lose the shape the flat view
 * established.
 *
 * The first pass used 232, which measured out at over half the radius: the one
 * grounded node in the demo towered over everything and the graph stopped reading
 * as a disc with relief. Proportion to the graph is the constraint, not visibility
 * of the axis — an axis you cannot miss has taken over the view it was added to.
 */
const TIER = 145

/**
 * How much contact with something outside the model this claim has had.
 *
 * Four tiers, and the ordering is about CONTACT rather than about agreement. A
 * claim a source contradicts sits at the same height as one a source supports:
 * both have been out of the building. Which way it went is already carried by
 * hue — magenta for disputed — and stacking that meaning onto height too would
 * make one axis answer two questions.
 *
 * A panel deliberately does not count. Three lenses disagreeing is three model
 * passes; it is the most interesting thing the model can tell you about itself
 * and it is still the model. Letting it lift a node would make the axis mean
 * "how much was spent on this" instead of "how much of this was checked".
 */
export function contactTier(n: ReasonNode): number {
  // A result you recorded is the top, and nothing the model can do reaches it.
  // It is also the one thing in a document another pass cannot reproduce.
  if (n.outcome) return 3
  // A reading off real sources. A documents-only check that produced one of the
  // four readings lands here too, and should: it read something outside the
  // model. The tier below is for a check that touched the corpus and came back
  // with no reading at all — contact was made, nothing was settled.
  //
  // A prior-art search on a conjecture lands here too, because a search did
  // happen: the tier says "checked against sources", never "supported", and a
  // search that found it stated sits beside one that did not, as a refutation
  // sits beside a support. Only a search of the current wording that completed.
  if (checkedAgainstSources(n) || priorArtSearched(n)) return 2
  if (n.verification?.corpus) return 1
  return 0
}

/** The height of a node on the evidence axis, in world units. */
export const contactHeight = (n: ReasonNode): number => contactTier(n) * TIER

/** The tiers, for the legend. Written here so the axis and its key cannot drift. */
export const CONTACT_TIERS: ReadonlyArray<{ tier: number; label: string }> = [
  { tier: 0, label: 'the model alone' },
  { tier: 1, label: 'your own documents' },
  { tier: 2, label: 'checked against sources' },
  { tier: 3, label: 'a result you recorded' },
]

export const TIER_HEIGHT = TIER

/* ── projection ─────────────────────────────────────────────────────────── */

export interface Projected {
  /** Screen pixels. */
  x: number
  y: number
  /**
   * Pixels per world unit at this point's depth.
   *
   * Every size in the spatial renderer is a world size times this — which is what
   * makes near things large and far things small without any per-mark decision.
   */
  s: number
  /** Distance in front of the eye, world units. Painter order and haze read it. */
  z: number
  /** False when the point is behind the near plane, where a perspective divide
   *  would fling it to the opposite side of the screen. */
  on: boolean
}

export interface Projector {
  project(x: number, y: number, z: number): Projected
  /** Screen position of the eye's own target, for the orientation furniture. */
  readonly cam: Cam
  readonly w: number
  readonly h: number
  /** World-space eye position. The renderer sorts and hazes by distance to it. */
  readonly eye: Vec3
  /** Unit vectors of the camera basis, exposed so callers can build billboards
   *  and lift bridge arcs toward the viewer without re-deriving the matrix. */
  readonly right: Vec3
  readonly up: Vec3
}

const NEAR = 24

/**
 * Build a projector for one frame.
 *
 * A closure over precomputed basis vectors rather than a class: it is rebuilt
 * every frame from the store's camera, and the only thing it is asked to do is
 * project a few thousand points as fast as possible.
 */
export function makeProjector(cam: Cam, w: number, h: number): Projector {
  const cp = Math.cos(cam.pitch)
  const sp = Math.sin(cam.pitch)
  const cy = Math.cos(cam.yaw)
  const sy = Math.sin(cam.yaw)

  // Forward: from the eye toward the target. `fy` is NEGATIVE for a positive
  // pitch, which is the sign that makes the eye sit above the plane and look down
  // — `eye = target - forward * dist`, so a downward forward puts the eye up.
  // Flip it and the whole view is upside down while every other formula here
  // still looks right, which is a long afternoon.
  const fx = cy * cp
  const fy = -sp
  const fz = sy * cp
  const eye: Vec3 = {
    x: cam.target.x - fx * cam.dist,
    y: cam.target.y - fy * cam.dist,
    z: cam.target.z - fz * cam.dist,
  }
  // Right is forward × worldUp, which stays well-conditioned because pitch is
  // clamped short of vertical — the reason `PITCH_MAX` is not π/2.
  const rx = -sy
  const rz = cy
  // Up completes the basis: right × forward.
  const ux = -rz * fy
  const uy = rz * fx - rx * fz
  const uz = rx * fy

  const focal = (FOCAL_AT_1000 * Math.max(320, w)) / 1000
  const cxp = w / 2
  const cyp = h / 2

  const project = (x: number, y: number, z: number): Projected => {
    const dx = x - eye.x
    const dy = y - eye.y
    const dz = z - eye.z
    const cz = dx * fx + dy * fy + dz * fz
    if (cz < NEAR) {
      return { x: 0, y: 0, s: 0, z: cz, on: false }
    }
    const cxv = dx * rx + dz * rz
    const cyv = dx * ux + dy * uy + dz * uz
    const s = focal / cz
    return { x: cxp + cxv * s, y: cyp - cyv * s, s, z: cz, on: true }
  }

  return {
    project,
    cam,
    w,
    h,
    eye,
    right: { x: rx, y: 0, z: rz },
    up: { x: ux, y: uy, z: uz },
  }
}

/* ── camera moves ───────────────────────────────────────────────────────── */

export const clampCam = (c: Cam): Cam => ({
  yaw: c.yaw,
  pitch: clamp(c.pitch, PITCH_MIN, PITCH_MAX),
  dist: clamp(c.dist, DIST_MIN, DIST_MAX),
  target: c.target,
})

export const orbit = (c: Cam, dYaw: number, dPitch: number): Cam =>
  clampCam({ ...c, yaw: c.yaw + dYaw, pitch: c.pitch + dPitch })

export const dolly = (c: Cam, factor: number): Cam => clampCam({ ...c, dist: c.dist * factor })

/**
 * Slide the target across the ground plane, in the camera's own screen axes.
 *
 * Screen-relative rather than world-relative because a pan that moved along world
 * X would send the graph diagonally off-screen at most yaws. The vertical
 * component moves along the projected ground direction rather than along the
 * evidence axis: dragging up should walk you across the plane, not levitate.
 */
export function pan(c: Cam, dxPx: number, dyPx: number, w: number): Cam {
  const perPx = (c.dist / ((FOCAL_AT_1000 * Math.max(320, w)) / 1000)) * 1.0
  const cy = Math.cos(c.yaw)
  const sy = Math.sin(c.yaw)
  // Screen-right on the ground plane.
  const rx = -sy
  const rz = cy
  // Screen-up projected onto the ground plane is the forward direction, flattened.
  const gx = cy
  const gz = sy
  return {
    ...c,
    target: {
      x: c.target.x - (dxPx * rx + dyPx * gx) * perPx,
      y: c.target.y,
      z: c.target.z - (dxPx * rz + dyPx * gz) * perPx,
    },
  }
}

/** Raise or lower the eye's target along the evidence axis. Bounded by the axis
 *  itself, so you cannot lose the graph by holding a key. */
export const raise = (c: Cam, dy: number): Cam => ({
  ...c,
  target: { ...c.target, y: clamp(c.target.y + dy, -TIER, TIER * 4) },
})

/**
 * How far inside the visible field a fitted graph's outermost claim is centred,
 * in pixels: clear of the tool dock, which stands 14px inside the field's right
 * edge, 48px wide, at the height where the disc is widest — and of the node's
 * own disc beside it. A pixel figure because both are fixed sizes on screen;
 * the 16% of the radius this replaced was 27px on a phone and 62 at 1440, so
 * the phone's outermost claim sat under the dock at every Fit. On the other
 * three sides it is a caption's room. The flat Fit gives the same figure up at
 * the one side the dock stands on (`fitField` in framing.ts).
 */
export const FIT_EDGE = 72

/**
 * A distance that fits a disc of the given radius in the visible field.
 *
 * Straight from the projection rather than tuned by eye: a world half-extent `E`
 * lands at `E · focal / d` pixels from the picture's centre, so holding it inside
 * the field's shorter half-axis less `FIT_EDGE` needs `d ≥ E·focal / (min(fw, fh)/2
 * − FIT_EDGE)`. The picture is centred in the field (`Spatial.aim`), so that is
 * the whole of the arithmetic.
 *
 * The FIELD, not the stage (D159): the stage runs under the sidebar, the Thread
 * and the composer, and at 1440 × 900 a disc sized to the stage's 900 came out
 * wider than the 680 the panes leave, into the Thread on one side and under the
 * dock and off the window on the other. The focal length stays the stage's, as
 * the projection's does — `w` is the stage width.
 *
 * The disc's full radius on both axes, though the tilt squashes it into an
 * ellipse: the fit holds at every yaw and pitch, so an orbit after Fit never
 * swings a branch out of the field. That is conservative in the direction of
 * showing everything.
 */
export function fitDistance(radius: number, w: number, fw: number, fh: number): number {
  const focal = (FOCAL_AT_1000 * Math.max(320, w)) / 1000
  const extent = Math.max(radius, 420)
  // Never less than half the half-axis: on a field `framing.ts` has already
  // squeezed to its floor, the edge would otherwise take the whole of it.
  const half = Math.max(1, Math.min(fw, fh)) / 2
  const room = Math.max(half / 2, half - FIT_EDGE)
  return clamp((extent * focal) / room, DIST_MIN, DIST_MAX)
}

/** Frame a graph of this radius, from the home angles, into a field of
 *  `fw` × `fh` on a stage `w` wide. */
export const camFor = (radius: number, w: number, fw: number, fh: number): Cam => ({
  ...CAM_HOME,
  dist: fitDistance(radius, w, fw, fh),
})

/* ── paths through the volume ───────────────────────────────────────────── */

/**
 * A strand from parent to child, in 3D.
 *
 * The horizontal curve is the SAME curve the 2D canvas draws — `strandPath` on
 * the ground plane with the same seed — so toggling views shows the same graph
 * rather than two drawings of one. Height interpolates with an ease rather than
 * linearly: a claim that has been checked sits above its unchecked parent, and a
 * straight ramp between them reads as a wire under tension where a curve reads as
 * the strand rising to it.
 */
export function liftPath(plane: Pt[], y0: number, y1: number): Vec3[] {
  const out: Vec3[] = []
  const n = plane.length - 1 || 1
  for (let i = 0; i < plane.length; i++) {
    const t = i / n
    // Smoothstep, so both ends leave their node horizontally.
    const e = t * t * (3 - 2 * t)
    out.push({ x: plane[i]!.x, y: y0 + (y1 - y0) * e, z: plane[i]!.y })
  }
  return out
}

/**
 * A bridge as an arc through the volume.
 *
 * This is the whole reason the view exists, so it is worth saying what the shape
 * is doing. The arc rises out of the plane by a fraction of the distance it
 * spans, which makes a bridge between two far-apart branches climb higher than
 * one between neighbours — the span becomes legible as altitude instead of as a
 * chord crossing everything in between. In 2D that chord is drawn straight across
 * the graph and reads as though it touched every branch it passed over.
 */
export function bridgeArc(a: Vec3, b: Vec3, steps = 30): Vec3[] {
  const dx = b.x - a.x
  const dz = b.z - a.z
  const span = Math.hypot(dx, dz)
  const lift = Math.min(1100, 210 + span * 0.42)
  const out: Vec3[] = []
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    // A parabola, which puts the apex at the middle and leaves both ends
    // tangent-ish to their nodes.
    const bow = 4 * t * (1 - t)
    out.push({
      x: a.x + dx * t,
      y: a.y + (b.y - a.y) * t + bow * lift,
      z: a.z + dz * t,
    })
  }
  return out
}

/**
 * Where a bridge into ANOTHER exploration goes.
 *
 * In 2D this cannot be drawn: the far end has no position on this canvas, so it
 * degrades to a dashed stub pointing outward at nothing. Here it gets somewhere
 * to point — a station well outside the graph's own disc, above the plane,
 * arranged by a hash of the foreign exploration's id so every reference to the
 * same one lands in the same place and two references to two different ones never
 * collide. It is still not that graph; it is an honest marker for where that graph
 * would be, and the renderer draws the strand to it dashed for exactly that
 * reason.
 */
export function foreignStation(seed: number, radius: number): Vec3 {
  const angle = seed * Math.PI * 2
  // Outside the disc but INSIDE the framing. The first pass put stations at
  // 1.42×+420, which on an ordinary graph is well beyond what `fitDistance`
  // frames — so the dashed strand always left the stage and you never saw where
  // it went, which is the one thing this marker exists to show. Far enough to be
  // obviously not part of this graph, near enough to be on screen when the graph
  // is.
  const r = radius * 1.12 + 200
  return { x: Math.cos(angle) * r, y: TIER * 1.6, z: Math.sin(angle) * r }
}
